import { Outlet, useOutletContext } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { MyProjectsRootPageDocument } from 'generated/resources/graphql'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import type { Tab } from 'modules/core/components/Card/types'
import NotFound from 'modules/core/components/NotFound'
import SectionContainer from 'modules/core/components/SectionContainer'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { SafePath } from 'modules/core/lib/navigation/context'

import { LoadingState } from 'modules/project_management/components/LoadingState'
import { useGetProjectsAssignedToWorker } from 'modules/project_management/hooks/projectWorker/useGetProjectsAssignedToWorker'
import CollectionsBootstrap from 'modules/project_management/lib/My_projectsCollectionsBootstrap'
import ProjectsStore from 'modules/project_management/store/models/Projects'

export interface RouteContextParams {
  tabs: Tab[]
  projectsStore: ProjectsStore
  employeeIdNumber: string
  employeeLocationId: string
  employeeName: string
  canRegisterFlexibleTracking: boolean
  hasTimeOffModule: boolean
  hasProjectCosts: boolean
  accessId: string
  trackingViewRoute: SafePath<string>
}

const Container = () => {
  const { company, employee } = useCurrentSession()
  const employeeId = String(employee?.id)

  const { data: page, loading: loadingPage } = useQuery(MyProjectsRootPageDocument, {
    variables: {
      companyId: company.id,
      employeeId: employeeId,
      featureIds: [Features.projects_flexible_tracking],
    },
    skip: !employee?.id,
  })

  const { projectsAssigned, loading: loadingProjects } = useGetProjectsAssignedToWorker({
    employeeIds: [employeeId],
    onlyActiveProjects: false,
    currentlyAssigned: false,
    includeSubprojects: true,
  })

  const hasFlexibleTracking = !!page?.companies.featureAvailabilitiesConnection.nodes.find(
    (node) => node.id === Features.projects_flexible_tracking
  )

  const outlet = useOutletContext<RouteContextParams>()

  const loading = loadingProjects || loadingPage

  if (loading) return <LoadingState />
  if (!employee || !page) return <NotFound />

  const projectsStore: ProjectsStore = new ProjectsStore({ projectsAssigned })
  const employeeLocationId = page.employees.employee?.locationId

  const canManageMyProjects =
    page.projectManagement._policies.flexibleTimeRecordRead.can ||
    page.projectManagement._policies.globalTimeRecordRead.can ||
    page.employees.employee?._policies?.registerFlexibleTracking.can ||
    false
  const isAuthorized =
    canManageMyProjects && projectsStore.hasProjectsAssigned && hasFlexibleTracking

  return (
    <AuthorizeUser authorizeFn={() => company && isAuthorized}>
      <Outlet
        context={{
          tabs: outlet?.tabs || [],
          projectsStore,
          employeeLocationId,
        }}
      />
    </AuthorizeUser>
  )
}

const ProjectsRootPage = () => {
  return (
    <CollectionsBootstrap>
      <SectionContainer>
        <Container />
      </SectionContainer>
    </CollectionsBootstrap>
  )
}

export default ProjectsRootPage
